import type { ExpectedCapitalRange, NeobankYieldDistribution, YaasUseCase } from "@/services/clientOnboarding";

export type UseCaseId = "treasury" | "pol" | "lp" | "neobank-d2u" | "other";
export type CapitalRange = "<500k" | "500k-2m" | "2m-10m" | "10m+";
export type DistributionModel = "pass-through" | "retained" | "programmatic";

export const USE_CASE_TO_BACKEND: Record<UseCaseId, YaasUseCase> = {
    treasury: "treasury",
    pol: "protocol_owned_liquidity",
    lp: "liquidity_provider",
    "neobank-d2u": "neobank",
    other: "other"
};

export const BACKEND_TO_USE_CASE: Partial<Record<YaasUseCase, UseCaseId>> = {
    treasury: "treasury",
    protocol_owned_liquidity: "pol",
    liquidity_provider: "lp",
    neobank: "neobank-d2u",
    other: "other"
};

export const CAPITAL_RANGE_TO_BACKEND: Record<CapitalRange, ExpectedCapitalRange> = {
    "<500k": "under_500k",
    "500k-2m": "500k_to_2m",
    "2m-10m": "2m_to_10m",
    "10m+": "over_10m"
};

export const BACKEND_TO_CAPITAL_RANGE: Record<ExpectedCapitalRange, CapitalRange> = {
    under_500k: "<500k",
    "500k_to_2m": "500k-2m",
    "2m_to_10m": "2m-10m",
    over_10m: "10m+"
};

export const DISTRIBUTION_TO_BACKEND: Record<DistributionModel, NeobankYieldDistribution> = {
    "pass-through": "pass_through",
    retained: "protocol_retains",
    programmatic: "protocol_distributes"
};

export const BACKEND_TO_DISTRIBUTION: Record<NeobankYieldDistribution, DistributionModel> = {
    pass_through: "pass-through",
    protocol_retains: "retained",
    protocol_distributes: "programmatic"
};

export interface UseCaseFormState {
    useCases: Set<UseCaseId>;
    capitalRange: CapitalRange | null;
    distribution: DistributionModel | null;
    lastSubmitted: {
        useCases: Set<UseCaseId>;
        capitalRange: CapitalRange | null;
        distribution: DistributionModel | null;
    } | null;
}

export const INITIAL_USE_CASE_STATE: UseCaseFormState = {
    useCases: new Set<UseCaseId>(),
    capitalRange: null,
    distribution: null,
    lastSubmitted: null
};

export function setsEqual(a: Set<UseCaseId>, b: Set<UseCaseId>): boolean {
    if (a.size !== b.size) return false;
    for (const v of a) if (!b.has(v)) return false;
    return true;
}

export function hasUseCaseFormDraft(state: UseCaseFormState): boolean {
    const { useCases, capitalRange, distribution, lastSubmitted } = state;
    const hasAnySelection = useCases.size > 0 || capitalRange !== null || distribution !== null;
    const isDirty =
        lastSubmitted !== null &&
        (!setsEqual(useCases, lastSubmitted.useCases) ||
            capitalRange !== lastSubmitted.capitalRange ||
            distribution !== lastSubmitted.distribution);
    return isDirty || (lastSubmitted === null && hasAnySelection);
}
